<script setup lang="ts">
import { computed, useSlots } from "vue";

const props = defineProps({
  loading: { type: Boolean, default: false },
  text: { type: String, required: false, default: "" },
  size: {
    default: "md",
    validator: (value: string) =>
      ["custom", "sm", "md", "lg", "xl", "2xl", "long"].includes(value),
  },
  image: { type: String, required: false, default: "" },
  contextualStyles: { type: Boolean, default: false },
});

const slots = useSlots();
const hasPrefix = computed(() => !!slots["image-prefix"]);
const hasSuffix = computed(() => !!slots["image-suffix"]);
const hasTextSlot = computed(() => !!slots["text"]);
const hasText = computed(() => !!props.text || hasTextSlot.value);

// add horizontal gap only when more than one visible part exists
const hasMultipleParts = computed(() => {
  const parts = [hasPrefix.value, hasText.value, hasSuffix.value].filter(
    Boolean
  );
  return parts.length > 1;
});

const extraClasses = computed(() => {
  // leading-none for icon-only to avoid baseline offset
  const leadingFix = hasText.value ? "" : "leading-none";
  switch (props.size) {
    case "custom":
      return [leadingFix];
    case "sm":
      return [
        "px-4 py-[3px] text-xs",
        hasText.value ? "leading-4" : "leading-none",
      ];
    case "md":
      return [
        "px-6 py-[3px] text-sm",
        hasText.value ? "leading-6" : "leading-none",
      ];
    case "lg":
      return [
        "px-6 py-[7px] text-lg",
        hasText.value ? "leading-6" : "leading-none",
      ];
    case "xl":
      return [
        "px-8 py-[15px] text-lg",
        hasText.value ? "leading-6" : "leading-none",
      ];
    case "2xl":
      return [
        "px-16 py-[31px] text-xl",
        hasText.value ? "leading-6" : "leading-none",
      ];
    case "long":
      return [
        "w-48 px-6 py-[3px] text-sm",
        hasText.value ? "leading-6" : "leading-none",
      ];
    default:
      return [
        "px-2 py-[3px] text-sm",
        hasText.value ? "leading-6" : "leading-none",
      ];
  }
});

const contextualClasses = computed(() =>
  props.contextualStyles ? "btn-submit--contextual" : ""
);
</script>

<template>
  <button
    type="submit"
    class="group enabled:opacity-100 opacity-50 flex items-center justify-center rounded-lm font-semibold shadow-xs enabled:cursor-pointer cursor-not-allowed transition-all duration-300 btn-submit btn-gold"
    :class="[extraClasses, contextualClasses]">
    <NuxtIcon name="svg-spinners:ring-resize" v-if="loading" class="w-12 h-5" />

    <template v-else>
      <div
        class="flex items-center uppercase whitespace-nowrap text-white"
        :class="hasMultipleParts ? 'gap-1' : ''">
        <div
          v-if="hasPrefix"
          class="group-enabled:contrast-100 contrast-0 flex items-center text-white">
          <slot name="image-prefix" />
        </div>

        <slot name="text">
          <p v-if="text" class="text-white truncate max-w-full">{{ text }}</p>
        </slot>

        <div
          v-if="hasSuffix"
          class="group-enabled:contrast-100 contrast-0 flex items-center text-white">
          <slot name="image-suffix" />
        </div>
      </div>
    </template>
  </button>
</template>

<style scoped>
.btn-submit {
  color: #fff;
}

.btn-submit :deep(svg),
.btn-submit :deep(svg *),
.btn-submit :deep(.nuxt-icon),
.btn-submit :deep(.nuxt-icon *) {
  color: #fff !important;
  fill: currentColor !important;
  stroke: currentColor !important;
}
</style>
